import React, { useEffect, useState } from 'react';
import { VimeoWidget, WIDGET_TYPE } from '../../../../types';
import { WidgetSettingsHeader } from '../../Styles';
import { useInspectorStore } from '../../../../stores/inspectorStore';
import {
  Card,
  InputField,
  Inspector,
  List,
  Tooltip,
} from '@bynder/design-system';
import {
  VimeoSettings,
  useVimeoWidgetMutations,
} from '../../../../mutations/widgets/vimeo';
import { Translate, useTranslate } from '@bynder/localization';
import { IconDelete, IconInfo } from '@bynder/icons';
import { useQuery } from '@tanstack/react-query';
import { useActiveWidget } from '../../../../queries/guides';
import { useDebouncedCallback } from 'use-debounce';
import { INSPECTOR_DEBOUNCE_DELAY } from '../constants';
import { emptyCSSObject } from '../../../../constants/widgets';
import MediaBorderRadius from '../MediaBorderRadius';
import { isDevelopment } from '../../../../constants/app';
import CustomCssStyleEditor from '../CustomCSSEditor';

const sourceIdToURL = (sourceId: string) =>
  sourceId ? `https://vimeo.com/${sourceId}` : '';

const vimeoUrlRegex = /https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/;

const isValidUrl = (url: string) => vimeoUrlRegex.test(url);

export const VimeoWidgetSettings: React.FC = () => {
  const { translate } = useTranslate();
  const vimeoWidget = useActiveWidget() as VimeoWidget;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { editSource, editSettings, editStyle } = useVimeoWidgetMutations(
    widgetSettings.sectionId,
    widgetSettings.columnIndex,
    widgetSettings.widgetId,
  );

  const [videoUrl, setVideoUrl] = useState(
    sourceIdToURL(vimeoWidget?.sourceId),
  );

  const onEditSourceId = (url: string) =>
    isValidUrl(url) && editSource.mutate({ widgetSource: extractVideoID(url) });

  const debouncedEditSourceId = useDebouncedCallback(
    onEditSourceId,
    INSPECTOR_DEBOUNCE_DELAY,
  );

  const handleUrlChange = (url: string) => {
    setVideoUrl(url);
    debouncedEditSourceId(url);
  };

  const handleLoopToggle = () => {
    editSettings.mutate({
      [VimeoSettings.loop]: !vimeoWidget?.settings?.loop,
    });
  };

  const handleAutoplayToggle = () => {
    editSettings.mutate({
      [VimeoSettings.autoplay]: !vimeoWidget?.settings?.autoplay,
    });
  };

  const handleDetailsToggle = () => {
    editSettings.mutate({
      [VimeoSettings.detailsShown]: !vimeoWidget?.settings?.detailsShown,
    });
  };

  const handleEditStyle = (style: string) => {
    editStyle.mutate({ style });
  };

  useEffect(() => {
    setVideoUrl(sourceIdToURL(vimeoWidget?.sourceId));
  }, [vimeoWidget?.sourceId]);

  return (
    <>
      <WidgetSettingsHeader widgetType={WIDGET_TYPE.VIMEO} />
      <List>
        {vimeoWidget?.sourceId && (
          <VimeoWidgetCard videoId={extractVideoID(videoUrl)} />
        )}
        <InputField
          label="Vimeo URL"
          placeholder="https://vimeo.com/375468729"
          isInvalid={!isValidUrl(videoUrl)}
          helperText={
            !isValidUrl(videoUrl) &&
            translate('WIDGET_INSPECTOR.BUTTON_LINK_INVALID_URL')
          }
          value={videoUrl}
          onChange={handleUrlChange}
          id="vimeoUrl"
        />
        <List.Item
          isDisabled={vimeoWidget?.sourceId === ''}
          checkedVariant="switch"
          isChecked={vimeoWidget?.settings?.detailsShown}
          onClick={handleDetailsToggle}
        >
          <Translate id="WIDGET_INSPECTOR.SHOW_DETAILS_BUTTON" />
        </List.Item>
        <List.Item
          isDisabled={vimeoWidget?.sourceId === ''}
          checkedVariant="switch"
          isChecked={vimeoWidget?.settings?.autoplay}
          onClick={handleAutoplayToggle}
        >
          <Tooltip
            content={translate('WIDGET_INSPECTOR.VIDEO_AUTPLAY_MUTED_TOOLTIP')}
            timing="delay"
          >
            <Translate id="WIDGET_INSPECTOR.VIDEO_AUTOPLAY_TOGGLE" />{' '}
            <IconInfo />
          </Tooltip>
        </List.Item>
        <List.Item
          isDisabled={vimeoWidget?.sourceId === ''}
          checkedVariant="switch"
          isChecked={vimeoWidget?.settings?.loop}
          onClick={handleLoopToggle}
        >
          <Translate id="WIDGET_INSPECTOR.VIDEO_LOOP_TOGGLE" />
        </List.Item>
        <Inspector.Divider />
        <MediaBorderRadius
          style={vimeoWidget?.style?.stylesheet ?? emptyCSSObject.stylesheet}
          onEditStyle={handleEditStyle}
        />

        {isDevelopment && (
          <>
            <Inspector.Divider />
            <CustomCssStyleEditor
              handleChange={() => editStyle.mutate({ style: '' })}
              label="Section CSS"
              placeholder="Enter your custom CSS here"
              style={vimeoWidget?.style?.stylesheet}
            />
          </>
        )}
      </List>
    </>
  );
};

const VimeoWidgetCard = ({ videoId }: { videoId: string }) => {
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { editSource } = useVimeoWidgetMutations(
    widgetSettings.sectionId,
    widgetSettings.columnIndex,
    widgetSettings.widgetId,
  );
  const handleClick = () => {};
  const { translate } = useTranslate();
  const clearVideo = () => editSource.mutate({ widgetSource: '' });

  const { data, isLoading } = useQuery({
    queryKey: [videoId],
    queryFn: () =>
      fetch(
        `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`,
      ).then(res => res.json()),
    enabled: !!videoId,
  });

  if (isLoading) return null;

  return (
    <Card
      variant="clean"
      onClick={handleClick}
      contextAction={
        <Card.ContextActionButton
          tooltip={translate('WIDGET_INSPECTOR.CLEAR_VIDEO_BUTTON')}
          aria-label="Clear video"
          onClick={clearVideo}
        >
          <IconDelete />
        </Card.ContextActionButton>
      }
    >
      <Card.Frame>
        <Card.Images>
          <Card.Img contentResize="fit" src={data?.thumbnail_url} />
        </Card.Images>
      </Card.Frame>

      <Card.Header title={data?.title} />
    </Card>
  );
};

function extractVideoID(input) {
  // Check if the provided input is a valid YouTube video ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }

  // Regular expression to match YouTube video URLs
  const youtubeUrlRegex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const youtubeShortsRegex =
    /https?:\/\/www\.youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/;

  // Regular expression to match Vimeo video URLs
  const vimeoUrlRegex = /https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/;

  const match = input.match(youtubeUrlRegex);
  const shortsMatch = input.match(youtubeShortsRegex);
  const vimeoMatch = input.match(vimeoUrlRegex);

  if (match && match[1]) {
    return match[1];
  }

  if (shortsMatch && shortsMatch[1]) {
    return shortsMatch[1];
  }

  if (vimeoMatch && vimeoMatch[1]) {
    return vimeoMatch[1];
  }

  // If no valid ID found, return null
  return null;
}
